import React from 'react';
import './App.css';
import UniversalHeader from './UniversalHeader';
import { supabase } from './supabaseClient';

function formatWhen(value) {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

function listingLabel(row) {
  const rel = row.properties;
  const prop = Array.isArray(rel) ? rel[0] : rel;
  if (prop?.name) {
    return prop.location ? `${prop.name} · ${prop.location}` : prop.name;
  }
  return (row.listing_title || '').trim() || '—';
}

function mapPropertyRow(row) {
  const msg = (row.message || '').trim();
  const preview =
    msg.length > 140 ? `${msg.slice(0, 140)}…` : msg || '—';
  return {
    id: row.id,
    name: row.full_name || '—',
    email: row.email || '—',
    phone: row.phone || '—',
    listing: listingLabel(row),
    messagePreview: preview,
    status: row.status || 'new',
    createdAt: row.created_at,
  };
}

const contactTopicLabel = {
  general: 'General enquiry',
  viewing: 'Book a viewing',
  tenancy: 'Existing tenancy',
  list: 'List a property',
  other: 'Other',
};

function mapContactRow(row) {
  const msg = (row.message || '').trim();
  const preview =
    msg.length > 140 ? `${msg.slice(0, 140)}…` : msg || '—';
  return {
    id: row.id,
    name: row.full_name || '—',
    email: row.email || '—',
    phone: row.phone || '—',
    topic: row.topic || 'general',
    topicLabel: contactTopicLabel[row.topic] || row.topic,
    messagePreview: preview,
    status: row.status || 'new',
    createdAt: row.created_at,
  };
}

const statusLabel = {
  new: 'New',
  read: 'Read',
  closed: 'Closed',
};

function Enquiries() {
  const [panel, setPanel] = React.useState('property');

  const [rows, setRows] = React.useState([]);
  const [contactRows, setContactRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [propertyError, setPropertyError] = React.useState('');
  const [contactError, setContactError] = React.useState('');
  const [search, setSearch] = React.useState('');
  const [filter, setFilter] = React.useState('all');

  const [detailId, setDetailId] = React.useState(null);
  const [detailSource, setDetailSource] = React.useState(null);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const [detailError, setDetailError] = React.useState('');
  const [detailData, setDetailData] = React.useState(null);
  const [markBusy, setMarkBusy] = React.useState(false);

  const closeDetail = React.useCallback(() => {
    setDetailId(null);
    setDetailSource(null);
    setDetailData(null);
    setDetailError('');
  }, []);

  const loadData = React.useCallback(async () => {
    if (!supabase) {
      setPropertyError('Supabase is not configured.');
      setContactError('');
      setRows([]);
      setContactRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setPropertyError('');
    setContactError('');

    const [peRes, cmRes] = await Promise.all([
      supabase
        .from('property_enquiries')
        .select(
          `
          id,
          property_id,
          listing_title,
          full_name,
          email,
          phone,
          message,
          status,
          created_at,
          properties (
            name,
            location
          )
        `
        )
        .order('created_at', { ascending: false }),
      supabase
        .from('contact_messages')
        .select(
          'id, full_name, email, phone, topic, message, status, created_at'
        )
        .order('created_at', { ascending: false }),
    ]);

    if (peRes.error) {
      setPropertyError(peRes.error.message);
      setRows([]);
    } else {
      setRows((peRes.data || []).map(mapPropertyRow));
    }

    if (cmRes.error) {
      setContactError(cmRes.error.message);
      setContactRows([]);
    } else {
      setContactRows((cmRes.data || []).map(mapContactRow));
    }

    setLoading(false);
  }, []);

  React.useEffect(() => {
    loadData();
  }, [loadData]);

  React.useEffect(() => {
    if (!detailId || !detailSource) return undefined;

    if (!supabase) {
      setDetailLoading(false);
      setDetailError('Supabase is not configured.');
      setDetailData(null);
      return undefined;
    }

    let cancelled = false;
    setDetailLoading(true);
    setDetailError('');
    setDetailData(null);

    const table =
      detailSource === 'contact' ? 'contact_messages' : 'property_enquiries';

    const select =
      detailSource === 'contact'
        ? 'id, full_name, email, phone, topic, message, status, created_at'
        : `
          id,
          property_id,
          listing_title,
          full_name,
          email,
          phone,
          message,
          status,
          created_at,
          properties (
            name,
            location
          )
        `;

    (async () => {
      const { data, error } = await supabase
        .from(table)
        .select(select)
        .eq('id', detailId)
        .single();

      if (cancelled) return;
      if (error) {
        setDetailError(error.message);
      } else {
        setDetailData(data);
      }
      setDetailLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [detailId, detailSource]);

  React.useEffect(() => {
    if (!detailId) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeDetail();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [detailId, closeDetail]);

  const openPropertyDetail = (r) => {
    setDetailSource('property');
    setDetailId(r.id);
  };

  const openContactDetail = (r) => {
    setDetailSource('contact');
    setDetailId(r.id);
  };

  const markAsRead = async () => {
    if (!detailData || !supabase || !detailSource || detailData.status !== 'new')
      return;
    setMarkBusy(true);
    try {
      const table =
        detailSource === 'contact' ? 'contact_messages' : 'property_enquiries';
      const { error } = await supabase
        .from(table)
        .update({ status: 'read' })
        .eq('id', detailData.id);
      if (error) throw error;
      setDetailData((d) => (d ? { ...d, status: 'read' } : d));
      await loadData();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      setDetailError(err?.message || 'Could not update status.');
    } finally {
      setMarkBusy(false);
    }
  };

  const setPanelAndReset = (next) => {
    setPanel(next);
    setSearch('');
    setFilter('all');
    closeDetail();
  };

  const filteredProperty = rows.filter((r) => {
    const term = search.trim().toLowerCase();
    const okSearch =
      !term ||
      r.name.toLowerCase().includes(term) ||
      r.email.toLowerCase().includes(term) ||
      r.listing.toLowerCase().includes(term) ||
      r.messagePreview.toLowerCase().includes(term);
    const okFilter = filter === 'all' || r.status === filter;
    return okSearch && okFilter;
  });

  const filteredContact = contactRows.filter((r) => {
    const term = search.trim().toLowerCase();
    const okSearch =
      !term ||
      r.name.toLowerCase().includes(term) ||
      r.email.toLowerCase().includes(term) ||
      r.topicLabel.toLowerCase().includes(term) ||
      r.messagePreview.toLowerCase().includes(term);
    const okFilter = filter === 'all' || r.status === filter;
    return okSearch && okFilter;
  });

  const activeList = panel === 'property' ? filteredProperty : filteredContact;
  const activeError = panel === 'property' ? propertyError : contactError;

  const initials = (name) => {
    const parts = (name || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase();
  };

  return (
    <div className="tenants-root">
      <UniversalHeader />

      <div className="tenants-shell">
        <header className="tenants-header">
          <div>
            <h1 className="tenants-title">Enquiries</h1>
            <p className="tenants-subtitle">
              {panel === 'property'
                ? 'Messages from the public listing enquiry form (/property/…/enquiry).'
                : 'Messages sent from the Contact us page (/contact).'}
            </p>
          </div>

          <div className="tenants-actions">
            <button type="button" className="btn-ghost tenants-btn" onClick={loadData}>
              Refresh
            </button>
          </div>
        </header>

        <div className="tenants-toolbar" style={{ marginTop: -8, marginBottom: 8 }}>
          <div className="tenants-filters">
            <button
              type="button"
              className={`tenants-filter-chip ${
                panel === 'property' ? 'tenants-filter-chip--active' : ''
              }`}
              onClick={() => setPanelAndReset('property')}
            >
              Listing enquiries
            </button>
            <button
              type="button"
              className={`tenants-filter-chip ${
                panel === 'contact' ? 'tenants-filter-chip--active' : ''
              }`}
              onClick={() => setPanelAndReset('contact')}
            >
              Contact page
            </button>
          </div>
        </div>

        {activeError ? (
          <p className="properties-save-error" role="alert">
            {activeError}
          </p>
        ) : null}

        <section className="tenants-toolbar">
          <div className="tenants-search">
            <div className="input-shell tenants-search-shell">
              <div className="input-icon">🔍</div>
              <input
                className="input-element"
                type="text"
                placeholder={
                  panel === 'property'
                    ? 'Search by name, email, listing, or message'
                    : 'Search by name, email, topic, or message'
                }
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          <div className="tenants-filters">
            {['all', 'new', 'read', 'closed'].map((key) => (
              <button
                key={key}
                type="button"
                className={`tenants-filter-chip ${
                  filter === key ? 'tenants-filter-chip--active' : ''
                }`}
                onClick={() => setFilter(key)}
              >
                {key === 'all' ? 'All' : statusLabel[key]}
              </button>
            ))}
          </div>
        </section>

        <main className="tenants-layout">
          <section className="tenants-list">
            <div className="tenants-list-header">
              <span className="tenants-list-label">
                {panel === 'property' ? 'Property enquiries' : 'Contact form'}
              </span>
              <span className="tenants-list-count">
                {loading ? '…' : `${activeList.length} total`}
              </span>
            </div>

            {loading ? (
              <p className="properties-list-empty">Loading…</p>
            ) : (
              <div className="tenants-grid">
                {activeList.length === 0 ? (
                  <p className="properties-list-empty">
                    {panel === 'property' ? (
                      <>
                        No listing enquiries yet. Run{' '}
                        <code>sql/add_property_enquiries_supabase.sql</code> in Supabase.
                      </>
                    ) : (
                      <>
                        No contact messages yet. Run{' '}
                        <code>sql/add_contact_messages_supabase.sql</code> in Supabase.
                      </>
                    )}
                  </p>
                ) : panel === 'property' ? (
                  filteredProperty.map((r) => (
                    <article className="tenants-card" key={r.id}>
                      <div className="tenants-card-main">
                        <div className="tenants-avatar">
                          <span>{initials(r.name)}</span>
                        </div>
                        <div className="tenants-main-text">
                          <h2 className="tenants-card-name">{r.name}</h2>
                          <p className="tenants-card-contact">
                            {r.email}
                            {r.phone && r.phone !== '—' ? ` · ${r.phone}` : ''}
                          </p>
                          <p className="tenants-card-property">{r.listing}</p>
                          <p className="tenants-card-property" style={{ marginTop: 6 }}>
                            {r.messagePreview}
                          </p>
                        </div>
                        <span className={`enquiries-status-badge enquiries-status-badge--${r.status}`}>
                          {statusLabel[r.status] || r.status}
                        </span>
                      </div>

                      <div className="tenants-card-meta">
                        <div className="tenants-meta-block">
                          <span className="tenants-meta-label">Received</span>
                          <span className="tenants-meta-value">{formatWhen(r.createdAt)}</span>
                        </div>
                        <div className="tenants-card-actions">
                          <button
                            type="button"
                            className="properties-icon-btn"
                            aria-label="View enquiry"
                            onClick={() => openPropertyDetail(r)}
                          >
                            👁
                          </button>
                        </div>
                      </div>
                    </article>
                  ))
                ) : (
                  filteredContact.map((r) => (
                    <article className="tenants-card" key={r.id}>
                      <div className="tenants-card-main">
                        <div className="tenants-avatar">
                          <span>{initials(r.name)}</span>
                        </div>
                        <div className="tenants-main-text">
                          <h2 className="tenants-card-name">{r.name}</h2>
                          <p className="tenants-card-contact">
                            {r.email}
                            {r.phone && r.phone !== '—' ? ` · ${r.phone}` : ''}
                          </p>
                          <p className="tenants-card-property">{r.topicLabel}</p>
                          <p className="tenants-card-property" style={{ marginTop: 6 }}>
                            {r.messagePreview}
                          </p>
                        </div>
                        <span className={`enquiries-status-badge enquiries-status-badge--${r.status}`}>
                          {statusLabel[r.status] || r.status}
                        </span>
                      </div>

                      <div className="tenants-card-meta">
                        <div className="tenants-meta-block">
                          <span className="tenants-meta-label">Received</span>
                          <span className="tenants-meta-value">{formatWhen(r.createdAt)}</span>
                        </div>
                        <div className="tenants-card-actions">
                          <button
                            type="button"
                            className="properties-icon-btn"
                            aria-label="View message"
                            onClick={() => openContactDetail(r)}
                          >
                            👁
                          </button>
                        </div>
                      </div>
                    </article>
                  ))
                )}
              </div>
            )}
          </section>
        </main>
      </div>

      {detailId && detailSource ? (
        <div
          className="properties-modal-root"
          role="presentation"
          onClick={closeDetail}
        >
          <div
            className="properties-modal-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="enquiry-detail-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="properties-modal-header">
              <h2 id="enquiry-detail-title" className="properties-modal-title">
                {detailSource === 'contact' ? 'Contact message' : 'Enquiry details'}
              </h2>
              <button
                type="button"
                className="properties-modal-close"
                aria-label="Close"
                onClick={closeDetail}
              >
                ×
              </button>
            </div>

            <div className="properties-modal-body">
              {detailLoading ? (
                <p className="properties-list-empty">Loading…</p>
              ) : null}
              {detailError ? (
                <p className="properties-save-error" role="alert">
                  {detailError}
                </p>
              ) : null}
              {!detailLoading && detailData && detailSource === 'property' ? (
                <>
                  <div className="properties-modal-section">
                    <h3 className="properties-modal-label">Listing</h3>
                    <p className="properties-modal-value">{listingLabel(detailData)}</p>
                  </div>
                  <div className="properties-modal-section">
                    <h3 className="properties-modal-label">Received</h3>
                    <p className="properties-modal-value">{formatWhen(detailData.created_at)}</p>
                  </div>
                  <div className="properties-modal-section">
                    <h3 className="properties-modal-label">Status</h3>
                    <p className="properties-modal-value">
                      <span
                        className={`enquiries-status-badge enquiries-status-badge--${detailData.status}`}
                      >
                        {statusLabel[detailData.status] || detailData.status}
                      </span>
                    </p>
                  </div>
                  <div className="properties-modal-grid">
                    <div className="properties-modal-section">
                      <h3 className="properties-modal-label">Full name</h3>
                      <p className="properties-modal-value">{detailData.full_name || '—'}</p>
                    </div>
                    <div className="properties-modal-section">
                      <h3 className="properties-modal-label">Email</h3>
                      <p className="properties-modal-value">{detailData.email || '—'}</p>
                    </div>
                    <div className="properties-modal-section">
                      <h3 className="properties-modal-label">Phone</h3>
                      <p className="properties-modal-value">{detailData.phone || '—'}</p>
                    </div>
                  </div>
                  <div className="properties-modal-section">
                    <h3 className="properties-modal-label">Message</h3>
                    <p className="properties-modal-value" style={{ whiteSpace: 'pre-wrap' }}>
                      {(detailData.message || '').trim() || '—'}
                    </p>
                  </div>
                </>
              ) : null}
              {!detailLoading && detailData && detailSource === 'contact' ? (
                <>
                  <div className="properties-modal-section">
                    <h3 className="properties-modal-label">Topic</h3>
                    <p className="properties-modal-value">
                      {contactTopicLabel[detailData.topic] || detailData.topic}
                    </p>
                  </div>
                  <div className="properties-modal-section">
                    <h3 className="properties-modal-label">Received</h3>
                    <p className="properties-modal-value">{formatWhen(detailData.created_at)}</p>
                  </div>
                  <div className="properties-modal-section">
                    <h3 className="properties-modal-label">Status</h3>
                    <p className="properties-modal-value">
                      <span
                        className={`enquiries-status-badge enquiries-status-badge--${detailData.status}`}
                      >
                        {statusLabel[detailData.status] || detailData.status}
                      </span>
                    </p>
                  </div>
                  <div className="properties-modal-grid">
                    <div className="properties-modal-section">
                      <h3 className="properties-modal-label">Full name</h3>
                      <p className="properties-modal-value">{detailData.full_name || '—'}</p>
                    </div>
                    <div className="properties-modal-section">
                      <h3 className="properties-modal-label">Email</h3>
                      <p className="properties-modal-value">{detailData.email || '—'}</p>
                    </div>
                    <div className="properties-modal-section">
                      <h3 className="properties-modal-label">Phone</h3>
                      <p className="properties-modal-value">{detailData.phone || '—'}</p>
                    </div>
                  </div>
                  <div className="properties-modal-section">
                    <h3 className="properties-modal-label">Message</h3>
                    <p className="properties-modal-value" style={{ whiteSpace: 'pre-wrap' }}>
                      {(detailData.message || '').trim() || '—'}
                    </p>
                  </div>
                </>
              ) : null}
            </div>

            <div className="properties-modal-footer">
              {detailData?.status === 'new' ? (
                <button
                  type="button"
                  className="btn-ghost properties-btn"
                  disabled={markBusy}
                  onClick={markAsRead}
                >
                  {markBusy ? 'Updating…' : 'Mark as read'}
                </button>
              ) : null}
              <button type="button" className="btn-primary properties-btn" onClick={closeDetail}>
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default Enquiries;
